/**
 * <covaso-lp-hero> — behaviour for sections/01-lp-hero.liquid.
 *
 * Kept in an asset rather than an inline <script> so it is downloaded once
 * and cached, instead of being re-sent inside every product page's HTML.
 * The image gallery itself is Horizon's native <media-gallery> (rendered via
 * snippets/product-media-gallery-content.liquid, same as the stock product
 * page) — this file only owns the bundle tiers, variant cards, sticky-bar
 * sync, and jumping the native gallery to a variant's photo on color select.
 *
 * Attributes: data-product-id (to find the sticky add-to-cart bar).
 */

/**
 * @param {Date} from
 * @param {number} days
 */
function addBusinessDays(from, days) {
  const date = new Date(from.getTime());
  let added = 0;
  while (added < days) {
    date.setDate(date.getDate() + 1);
    const weekday = date.getDay();
    if (weekday !== 0 && weekday !== 6) added++;
  }
  return date;
}

class CovasoLpHero extends HTMLElement {
  #abort = new AbortController();
  /** @type {Set<ReturnType<typeof setInterval>>} */
  #intervals = new Set();
  /** @type {IntersectionObserver[]} */
  #observers = [];
  #ready = false;

  connectedCallback() {
    if (this.#ready) return;
    this.#ready = true;
    this.#initTiers();
    this.#initVariantCards();
    this.#initReviewCarousel();
    this.#initDelivery();
    this.#initRatingLink();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#intervals.forEach((id) => clearInterval(id));
    this.#intervals.clear();
    this.#observers.forEach((observer) => observer.disconnect());
    this.#observers = [];
  }

  /**
   * The price block, "Save …" pill and installment line follow whichever
   * tier or variant card is selected.
   * @param {HTMLElement} card
   */
  #updateDisplay(card) {
    const price = this.querySelector('[data-lp-price]');
    if (price && card.dataset.price) price.textContent = card.dataset.price;

    const compare = /** @type {HTMLElement | null} */ (this.querySelector('[data-lp-compare]'));
    if (compare) {
      compare.textContent = card.dataset.compare || '';
      compare.hidden = !card.dataset.compare;
    }

    const save = /** @type {HTMLElement | null} */ (this.querySelector('[data-lp-save]'));
    if (save) {
      save.textContent = card.dataset.save || '';
      save.hidden = !card.dataset.save;
    }

    const installment = /** @type {HTMLElement | null} */ (this.querySelector('[data-lp-installment-text]'));
    if (installment && card.dataset.installment) {
      installment.textContent = (installment.dataset.template || '').replace('[amount]', card.dataset.installment);
    }
  }

  #initTiers() {
    const group = this.querySelector('[data-lp-tiers]');
    if (!group) return;
    const tiers = /** @type {HTMLElement[]} */ ([...group.querySelectorAll('.covaso-hero-tier')]);

    // The theme's own quantity input if one is rendered; otherwise
    // product-form.js falls back to data-quantity-default.
    /** @param {HTMLElement} tier */
    const applyQty = (tier) => {
      const qty = tier.dataset.qty || '1';
      this.querySelectorAll('product-form-component').forEach((form) => {
        const input = /** @type {HTMLInputElement | null} */ (
          form.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );
        if (input) {
          input.value = qty;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          /** @type {HTMLElement} */ (form).dataset.quantityDefault = qty;
        }
      });
      // Buy-it-now forms live both in this section and the sticky bar.
      document.querySelectorAll('[data-covaso-buy-now-qty]').forEach((input) => {
        /** @type {HTMLInputElement} */ (input).value = qty;
      });
    };

    tiers.forEach((tier) => {
      tier.addEventListener('click', () => {
        tiers.forEach((t) => t.setAttribute('aria-pressed', String(t === tier)));
        applyQty(tier);
        this.#updateDisplay(tier);
      });
    });
  }

  #initVariantCards() {
    const group = this.querySelector('[data-lp-variants]');
    if (!group) return;
    const cards = /** @type {HTMLElement[]} */ ([...group.querySelectorAll('.custom-variant-tier')]);
    const splitOptions = this.querySelector('[data-lp-split-options]');
    const matrix = /** @type {HTMLElement[]} */ (
      splitOptions ? [...splitOptions.querySelectorAll('[data-lp-variant-option]')] : []
    );

    // Opening the picked pack and closing whichever one was open needs to start on
    // the exact same frame — closing immediately while the open side waited out
    // assets/covaso-lp.js's paint-then-expand delay (its double rAF, meant to stop
    // the very first open from snapping) made the whole tier stack visibly dip
    // before recovering. So both sides are driven from one synced callback here,
    // reusing that module's constants/technique but not its click handler, which
    // only ever owns one <details> at a time.
    const ACCORDION_ANIMATION_MS = 230;
    /**
     * @param {HTMLElement} toOpen
     * @param {HTMLElement[]} toClose
     */
    const syncAccordions = (toOpen, toClose) => {
      toClose.forEach((details) => {
        details.dataset.lpWant = 'closed';
      });
      if (toOpen.dataset.lpWant !== 'open') {
        toOpen.dataset.lpWant = 'open';
        toOpen.setAttribute('open', '');
      }
      const sync = () => {
        if (toOpen.dataset.lpWant === 'open') toOpen.classList.add('is-expanded');
        toClose.forEach((details) => {
          if (details.dataset.lpWant === 'closed') details.classList.remove('is-expanded');
        });
      };
      requestAnimationFrame(() => requestAnimationFrame(sync));
      setTimeout(sync, 80);
      toClose.forEach((details) => {
        setTimeout(() => {
          if (details.dataset.lpWant === 'closed') details.removeAttribute('open');
        }, ACCORDION_ANIMATION_MS + 80);
      });
    };

    /**
     * @param {HTMLElement} card
     * @returns {HTMLElement | undefined}
     */
    const findMatrixOption = (card) => matrix.find((candidate) => candidate.dataset.variantId === card.dataset.variantId);

    /**
     * Jumps the native <media-gallery>'s slideshow to a photo by index, rather
     * than fetching a re-rendered section — the index is precomputed
     * server-side (data-media-index) to match the gallery's own sorted_media
     * order (see snippets/product-media-gallery-content.liquid).
     * @param {string | undefined} mediaIndexRaw
     */
    const jumpGalleryToIndex = (mediaIndexRaw) => {
      const mediaIndex = parseInt(mediaIndexRaw ?? '-1', 10);
      if (mediaIndex < 0) return;
      const gallery = /** @type {import('./media-gallery').MediaGallery | null} */ (
        this.querySelector('media-gallery')
      );
      gallery?.slideshow?.select(mediaIndex, undefined, { animate: false });
    };

    /** @param {HTMLElement} card */
    const updateMainImage = (card) => jumpGalleryToIndex(findMatrixOption(card)?.dataset.mediaIndex);

    /** @param {HTMLElement} card */
    const selectCard = (card) => {
      if (card.dataset.variantAvailable === 'false') return;
      const variantId = card.dataset.variantId || '';

      const toClose = [];
      cards.forEach((candidate) => {
        const isTarget = candidate === card;
        candidate.setAttribute('aria-pressed', String(isTarget));
        if (!isTarget && candidate.dataset.lpWant === 'open') toClose.push(candidate);
      });
      syncAccordions(card, toClose);

      const idInput = /** @type {HTMLInputElement | null} */ (
        this.querySelector('.covaso-lp-hero__form product-form-component [name="id"]')
      );
      if (idInput) {
        idInput.value = variantId;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      document.querySelectorAll('[data-covaso-buy-now-id]').forEach((input) => {
        /** @type {HTMLInputElement} */ (input).value = variantId;
      });
      const buyNowWrap = /** @type {HTMLElement | null} */ (this.querySelector('.covaso-lp-hero__buy-now'));
      if (buyNowWrap) {
        buyNowWrap.hidden = false;
        const buyNowBtn = buyNowWrap.querySelector('button');
        if (buyNowBtn) buyNowBtn.disabled = false;
      }

      this.#updateDisplay(card);
      this.#syncStickyBar(card, findMatrixOption(card));
      updateMainImage(card);
      setPairPropertyInputs(card);
    };

    cards.forEach((card) => {
      const trigger = card.querySelector('[data-lp-tier-select]');
      if (!trigger) {
        card.addEventListener('click', () => selectCard(card));
        return;
      }
      // Split-variant cards are <details data-lp-accordion>, but the open/close here
      // is fully owned by syncAccordions above (not assets/covaso-lp.js's click
      // handler) so the two animations stay in lockstep — always block the native
      // toggle and stop it from also reaching that document-level listener.
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (card.dataset.variantAvailable === 'false' || card.getAttribute('aria-pressed') === 'true') return;
        selectCard(card);
      });
    });

    // "Pack" counts pieces, not pairs — a 4/8 Pack covers 2/4 windows, so it gets
    // one color picker per pair instead of one color for the whole box. Only pair 1
    // is tied to a real Shopify variant (color+bundle); pairs 2+ are pure line-item
    // properties, since there's no per-pair inventory to model.
    /** @param {HTMLElement} card */
    const setPairPropertyInputs = (card) => {
      const pairCount = parseInt(card.dataset.pairCount || '1', 10);
      document.querySelectorAll('[data-lp-pair-property], [data-covaso-buy-now-pair-property]').forEach((input) => {
        const pair = parseInt(
          /** @type {HTMLElement} */ (input).dataset.lpPairProperty ??
            /** @type {HTMLElement} */ (input).dataset.covasoBuyNowPairProperty ??
            '0',
          10
        );
        const pairEl = card.querySelector(`[data-lp-pair="${pair}"]`);
        const color = pairEl?.querySelector('[data-lp-color][aria-pressed="true"]')?.dataset.lpColor || '';
        /** @type {HTMLInputElement} */ (input).disabled = pair > pairCount || pairCount < 2;
        /** @type {HTMLInputElement} */ (input).value = color;
      });
    };

    if (!splitOptions) return;
    const colorButtons = /** @type {HTMLElement[]} */ ([...splitOptions.querySelectorAll('[data-lp-color]')]);

    colorButtons.forEach((colorButton) => {
      colorButton.addEventListener('click', () => {
        const card = /** @type {HTMLElement | null} */ (colorButton.closest('.custom-variant-tier'));
        const pairEl = /** @type {HTMLElement | null} */ (colorButton.closest('[data-lp-pair]'));
        if (!card || !pairEl) return;
        const color = colorButton.dataset.lpColor || '';
        const isFirstPair = pairEl.dataset.lpPair === '1';

        // Reset selection within this pair's own row only — each pair picks independently.
        pairEl.querySelectorAll('[data-lp-color]').forEach((button) => {
          button.setAttribute('aria-pressed', String(button === colorButton));
        });
        const colorLabel = pairEl.querySelector('[data-lp-color-label]');
        if (colorLabel) colorLabel.textContent = color;

        // Every pair previews its own color in the main image, even though only
        // pair 1 maps to a real purchasable variant (see the block comment above
        // setPairPropertyInputs).
        jumpGalleryToIndex(colorButton.dataset.mediaIndex);

        setPairPropertyInputs(card);
        if (!isFirstPair) return; // pairs 2+ are just properties; the variant/price come from pair 1

        const option = matrix.find(
          (candidate) => candidate.dataset.color === color && candidate.dataset.bundle === card.dataset.bundle
        );
        if (!option) return;
        ['variantId', 'variantTitle', 'variantAvailable', 'price', 'compare', 'save', 'installment'].forEach((key) => {
          card.dataset[key] = option.dataset[key] || '';
        });
        const available = option.dataset.variantAvailable !== 'false';
        card.classList.toggle('is-unavailable', !available);
        card.setAttribute('aria-disabled', String(!available));
        const price = card.querySelector('.covaso-hero-tier__price');
        if (price) price.textContent = option.dataset.price || '';
        const compare = card.querySelector('.covaso-hero-tier__compare');
        if (compare) {
          compare.textContent = option.dataset.compare || '';
          compare.toggleAttribute('hidden', !option.dataset.compare);
        }
        const highlight = card.querySelector('.covaso-hero-tier__highlight');
        if (highlight && option.dataset.save) highlight.textContent = option.dataset.save;
        if (available) selectCard(card);
      });
    });

    // Every pack already rendered its own pairs' default colors server-side; the
    // property inputs just need to start in sync with whichever pack is open.
    setPairPropertyInputs(cards.find((card) => card.getAttribute('aria-pressed') === 'true') || cards[0]);
  }

  /**
   * The sticky bar (sections/covaso-sticky-add-to-cart.liquid) keeps its own
   * variant state and normally hears about changes through the theme's
   * ProductSelectEvent, which these cards don't fire.
   * @param {HTMLElement} card
   * @param {HTMLElement | undefined} [matrixOption] - The matching [data-lp-variant-option], for its image.
   */
  #syncStickyBar(card, matrixOption) {
    const stickyBar = /** @type {HTMLElement | null} */ (
      document.querySelector(`sticky-add-to-cart[data-product-id="${this.dataset.productId}"]`)
    );
    if (!stickyBar) return;

    stickyBar.dataset.currentVariantId = card.dataset.variantId || '';
    stickyBar.dataset.variantAvailable = 'true';

    const info = /** @type {HTMLElement | null} */ (stickyBar.querySelector('.covaso-sticky-add-to-cart__info'));
    const label = stickyBar.querySelector('.covaso-sticky-add-to-cart__variant');
    if (label && info && info.dataset.singleton !== 'true') label.textContent = card.dataset.variantTitle || '';

    const stickyImage = /** @type {HTMLImageElement | null} */ (
      stickyBar.querySelector('.covaso-sticky-add-to-cart__image-img')
    );
    if (stickyImage && matrixOption?.dataset.imageUrl) {
      stickyImage.src = matrixOption.dataset.imageUrl;
      if (matrixOption.dataset.imageSrcset) stickyImage.srcset = matrixOption.dataset.imageSrcset;
    }

    const priceBox = stickyBar.querySelector('.covaso-sticky-add-to-cart__price');
    if (priceBox && card.dataset.price) {
      const price = document.createElement('span');
      price.className = 'price';
      price.textContent = card.dataset.price;
      priceBox.replaceChildren(price);
      if (card.dataset.compare) {
        const compare = document.createElement('span');
        compare.className = 'compare-at-price';
        compare.textContent = card.dataset.compare;
        priceBox.appendChild(compare);
      }
    }

    const button = /** @type {HTMLButtonElement | null} */ (stickyBar.querySelector('.covaso-sticky-add-to-cart__button'));
    if (button) button.disabled = false;
    const buyNowWrap = /** @type {HTMLElement | null} */ (stickyBar.querySelector('.covaso-sticky-add-to-cart__buy-now'));
    if (buyNowWrap) {
      buyNowWrap.hidden = false;
      const buyNowBtn = buyNowWrap.querySelector('button');
      if (buyNowBtn) buyNowBtn.disabled = false;
    }
  }

  #initReviewCarousel() {
    const carousel = /** @type {HTMLElement | null} */ (this.querySelector('[data-lp-review-carousel]'));
    if (!carousel) return;
    const slides = [...carousel.querySelectorAll('[data-lp-review-slide]')];
    const dots = /** @type {HTMLElement[]} */ ([...carousel.querySelectorAll('[data-lp-review-dot]')]);
    if (slides.length < 2) return;

    const interval = parseInt(carousel.dataset.interval || '', 10) || 5000;
    let index = 0;
    /** @type {ReturnType<typeof setInterval> | null} */
    let timer = null;

    /** @param {number} next */
    const show = (next) => {
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
        if (i === index) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    };
    const stop = () => {
      if (!timer) return;
      clearInterval(timer);
      this.#intervals.delete(timer);
      timer = null;
    };
    const start = () => {
      stop();
      timer = setInterval(() => show(index + 1), interval);
      this.#intervals.add(timer);
    };

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        show(parseInt(dot.dataset.lpReviewDot || '0', 10));
        start();
      });
    });
    // Hold still while someone is reading or tabbing through it.
    carousel.addEventListener('mouseenter', stop);
    carousel.addEventListener('mouseleave', start);
    carousel.addEventListener('focusin', stop);
    carousel.addEventListener('focusout', start);

    // Rotate from the start; the observer only pauses it while it's scrolled
    // out of view, so a missed intersection can't leave it frozen.
    start();
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[entries.length - 1]?.isIntersecting) start();
          else stop();
        },
        { threshold: 0.4 }
      );
      observer.observe(carousel);
      this.#observers.push(observer);
    }
  }

  #initDelivery() {
    const el = /** @type {HTMLElement | null} */ (this.querySelector('[data-lp-delivery]'));
    const text = el?.querySelector('[data-lp-delivery-text]');
    if (!el || !text) return;
    const min = parseInt(el.dataset.min || '', 10) || 1;
    const max = parseInt(el.dataset.max || '', 10) || min;
    const now = new Date();
    const format = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    text.textContent = '';
    (el.dataset.template || '').split(/(\[start\]|\[end\])/).forEach((part) => {
      if (part === '[start]' || part === '[end]') {
        const strong = document.createElement('strong');
        strong.textContent = format.format(addBusinessDays(now, part === '[start]' ? min : max));
        text.appendChild(strong);
      } else if (part) {
        text.appendChild(document.createTextNode(part));
      }
    });
    el.hidden = false;
  }

  #initRatingLink() {
    this.querySelector('[data-lp-rating-link]')?.addEventListener('click', () => {
      document.querySelector('.covaso-lp-reviews')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }
}

if (!customElements.get('covaso-lp-hero')) {
  customElements.define('covaso-lp-hero', CovasoLpHero);
}
